import { useEffect, useRef } from 'react';
import { MediaPlayer, MediaProvider, type MediaPlayerInstance } from '@vidstack/react';
import { defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';

interface VideoPlayerProps {
  src: string;
  title?: string;
  /** Retoma a partir deste segundo (de onde o usuário parou). */
  startTime?: number;
  /** Chamado de forma throttled (a cada ~5s) com a posição atual. */
  onProgress?: (segundos: number) => void;
  /** Chamado quando o vídeo termina. */
  onEnded?: () => void;
  /** Chamado uma vez com a duração total detectada. */
  onDuration?: (segundos: number) => void;
}

export function VideoPlayer({ src, title, startTime = 0, onProgress, onEnded, onDuration }: VideoPlayerProps) {
  const ref = useRef<MediaPlayerInstance>(null);
  const lastSaved = useRef(0);
  const resumed = useRef(false);
  const durationSent = useRef(false);

  useEffect(() => {
    // novo vídeo: zera os controles de retomada/throttle
    resumed.current = false;
    durationSent.current = false;
    lastSaved.current = 0;
    const player = ref.current;
    if (!player) return;

    return player.subscribe(({ currentTime, duration, canPlay }) => {
      if (duration && !durationSent.current) {
        durationSent.current = true;
        onDuration?.(Math.floor(duration));
      }
      if (canPlay && !resumed.current) {
        resumed.current = true;
        if (startTime > 0 && (!duration || startTime < duration - 2)) {
          player.currentTime = startTime;
        }
      }
      if (onProgress && currentTime - lastSaved.current >= 5) {
        lastSaved.current = currentTime;
        onProgress(Math.floor(currentTime));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return (
    <MediaPlayer
      ref={ref}
      title={title}
      src={src}
      playsInline
      className="aspect-video w-full overflow-hidden rounded-lg bg-black"
      onEnded={() => onEnded?.()}
    >
      <MediaProvider />
      <DefaultVideoLayout icons={defaultLayoutIcons} />
    </MediaPlayer>
  );
}
